import React from 'react';
import PropTypes from 'prop-types';
import SmoothScroll from 'smooth-scroll';

import withStyles from '@material-ui/core/styles/withStyles';
import MuiLink from '@material-ui/core/Link';
import Fade from '@material-ui/core/Fade';
import Skeleton from '@material-ui/lab/Skeleton';
import Backdrop from '@material-ui/core/Backdrop';

import FollowLogoImage from '../../../images/business/follow_logo.png';
import Logo from '../../core/Businesses/Logo';
import BusinessInfo, { styles as baseStyles } from '../../core/Businesses/Info';
import { createBusinessPageShareText } from '../../core/Social/TwitterButton';
import FollowButton from './Follow/FollowButton';
import T from 'i18n-react';
import withTheme from '@material-ui/core/styles/withTheme';
import SnsButtonGroup from '../../core/Businesses/SnsButtonGroup';
import { APP_VIEW_BUSINESSES } from '../routing';

const styles = (theme) => ({
  ...baseStyles(theme),
  followButtonSkeleton: {
    borderRadius: theme.shape.borderRadius,
    [theme.breakpoints.up('sm')]: {
      maxWidth: 400,
    },
  },
  followButtonWrapper: {
    width: '100%',
    position: 'relative',
    zIndex: theme.zIndex.appBar - 50,
    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(2),
    },
    [theme.breakpoints.up('sm')]: {
      marginLeft: 'auto',
      maxWidth: 200,
    },
  },
  followButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    zIndex: theme.zIndex.drawer + 2,
    position: 'relative',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
  tipButton: {
    backgroundColor: theme.palette.common.white,
    border: '5px solid #FFF',
  },
  tip: {
    zIndex: theme.zIndex.drawer + 2,
    position: 'absolute',
  },
  image: {
    width: '100%',
    height: 'auto',
  },
  followAnchor: {
    display: 'none',
    [theme.breakpoints.down('xs')]: {
      width: '90%',
      display: 'block',
      position: 'fixed',
      top: '200px',
      zIndex: theme.zIndex.drawer + 2,
    },
  },
});

class Info extends BusinessInfo {
  constructor(props) {
    super(props);
    this.state = {
      icon: true,
      tipDisplay: true,
    };
    new SmoothScroll('a[href*="#"]', {
      offset: 250,
    });
    this.closePopper = this.closePopper.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this._mounted = false;
  }

  componentDidMount() {
    this._mounted = true;
    window.addEventListener('scroll', this.handleScroll, true);
  }

  componentWillUnmount() {
    this._mounted = false;
    window.removeEventListener('scroll', this.handleScroll, true);
  }

  closePopper() {
    this.setState({ tipDisplay: false });
  }

  handleScroll() {
    if (this._mounted && window.pageYOffset > 100) {
      const { icon } = this.state;
      icon && this.setState({ icon: false });
    }
  }

  mainContentRenderer(): * {
    const {
      classes,
      loaded,
      logo,
      id,
      url,
      address,
      authenticated,
      followerCount,
      isFollowing,
      tipMessage,
      tipAlreadyShown,
      canShowShareButton,
    } = this.props;
    const { icon, tipDisplay } = this.state;
    const name = this.getName();
    const regionName = address?.region || '';
    const thisPageUrl = window.location.href.split(/[?#]/)[0];
    const twitterShareText = createBusinessPageShareText(
      name,
      thisPageUrl,
      regionName,
    );
    const isNotAppView =
      !window.location.pathname.includes(APP_VIEW_BUSINESSES);

    return (
      <React.Fragment>
        {tipDisplay && !tipAlreadyShown && (
          <Fade in={icon === true} timeout={500}>
            <MuiLink href="#stickerTipAnchor" className={classes.followAnchor}>
              <img
                src={FollowLogoImage}
                className={classes.image}
                alt={T.translate('follow')}
              />
            </MuiLink>
          </Fade>
        )}
        <Backdrop
          open={tipDisplay && !tipAlreadyShown}
          onClick={this.closePopper}
          className={classes.backdrop}
        />
        {/* 本来は、ページごとのコンポーネントの分岐をしたくない */}
        {canShowShareButton && (
          <SnsButtonGroup
            hiddenProps={{ only: 'xs' }}
            loaded={loaded}
            gutterBottom
            tweetText={twitterShareText}
            url={thisPageUrl}
          />
        )}
        <div className={classes.followButtonContainer}>
          <div>
            <Logo src={logo} alt={name} loaded={loaded} />
          </div>
          <div className={classes.xsName}>{this.nameRenderer(name, url)}</div>
          {isNotAppView && (
            <div className={classes.followButtonWrapper}>
              {loaded ? (
                <div
                  className={
                    tipDisplay && !tipAlreadyShown ? classes.tipButton : null
                  }
                >
                  <FollowButton
                    id="businessFollow"
                    businessId={id}
                    businessName={name}
                    isAuthenticated={authenticated}
                    followerCount={followerCount}
                    isFollowing={isFollowing}
                    onChange={this.closePopper}
                    isFollowerShown
                  />
                </div>
              ) : (
                <Skeleton
                  variant="rect"
                  width="100%"
                  height={40}
                  className={classes.followButtonSkeleton}
                />
              )}
            </div>
          )}
        </div>
        <div className={classes.smName}>{this.nameRenderer(name, url)}</div>
      </React.Fragment>
    );
  }
}

Info.propTypes = {
  ...BusinessInfo.propTypes,
  authenticated: PropTypes.bool,
  followerCount: PropTypes.number,
  isFollowing: PropTypes.bool,
  tipMessage: PropTypes.string,
  tipAlreadyShown: PropTypes.bool,
  canShowShareButton: PropTypes.bool,
};

export default withStyles(styles)(withTheme(Info));
